import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Box,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface IProps {
  question: string;
  answer: string;
}

const FAQ_ITEMS: IProps[] = [
  {
    question:
      'I have EFMP family members or specific amenity requests, and we require certain lodging considerations (ex. needing a tub, larger parking space, wheelchair accessibility), what should I do?',
    answer:
      'The Housing Assistance Center is only able to provide the hotel, any amenities and needs must be worked through with the hotel.',
  },
  {
    question:
      'If my hotel is on the approved TLA hotel list, but isn’t offering me the TLA rate, will I be refunded in full? Or do I need additional documentation?',
    answer:
      'It is currently recommended to continue searching for a hotel offering TLA rates. There is no guarantee that there will be reimbursement over the TLA rate. If you are unable to procure a room yourself, you may contact the Housing Assistance Center to update your preference for a government procured room. 808-789-5298',
  },
  {
    question:
      'If I changed my status from government procured lodging to either self procured housing or staying at home, what should I do?',
    answer:
      'If the Housing Assistance Center procured lodging for you and there is a change, please notify the Housing Assistance Center so they can annotate that status: 808-789-5287. If you have already checked in, be sure to communicate your intention with the hotel directly.',
  },
  {
    question: 'What should I do with my pets?',
    answer:
      'If you need to bring your pets with you, consult with the hotel to identify potential restrictions. Common restrictions include number of animals and their weight. Currently there are no kennels for pets available.',
  },
  {
    question: 'How do I verify that I have a hotel room?',
    answer:
      'Hotels are verifying information in person and are not accepting calls, you must be in person to check in/secure the room(s). Please allow a minimum processing time of 3 hours from the time you speak to a Housing Assistance Center representative before you arrive at the hotel.',
  },
  {
    question:
      'Do I need a COVID19 vaccine or negative test to check in to the hotel',
    answer:
      'Be prepared for hotels to require proof of vaccine or a negative test to check in to rooms.',
  },
  {
    question:
      'What do I do if I’m experiencing symptoms? (ex. diarrhea, cramps, rashes etc)',
    answer:
      'Those actively experiencing symptoms related to the water situation and believe they need treatment should contact either central appointment line (1-888-683-2778) or the Nurse Advice Line (1-800-874-2273) for an appointment.',
  },
  {
    question: 'Am I authorized to use my GTC?',
    answer:
      'GTC cards are authorized for use, if there are any concerns contact the sponsor’s chain of command.',
  },
  {
    question: 'Who do I file my claim with?',
    answer: 'You will need to file your claim with your specific service.',
  },
  {
    question:
      'If I’m in a Mil to Mil marriage, are we both qualified for per diem and TLA? ',
    answer:
      'Mil to Mil members are both authorized for per diem, and lodging TLA is split between the members or one member will claim the lodging. If there are dependents, one member may claim the dependent(s) and the other would file single',
  },
  {
    question:
      'My spouse is an Air National Guard member, can I claim them as a dependent for TLA?',
    answer:
      'If you are active duty and your spouse is a Guard member NOT on active duty orders, you may claim them for TLA',
  },
  {
    question:
      'I am a non-military spouse, can I file for TLA if my military spouse is unavailable?',
    answer:
      'Filing for TLA as a non-military spouse requires a Special Power of Attorney. Non-military spouses with a General Power of Attorney are unable to file for TLA.',
  },
];

const Question = ({ question, answer }: IProps) => {
  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Typography>{question}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: '#f7f7f7' }}>
        <Typography paddingLeft='40px'>{answer}</Typography>
      </AccordionDetails>
    </Accordion>
  );
};

const Questions = () => {
  return (
    <Box sx={{ paddingX: '20px' }}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1a-content'
          id='panel1a-header'
        >
          <Typography>
            My community has not been officially listed as an affected area,
            what should I do?
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: '#f7f7f7' }}>
          <Typography paddingLeft='40px' paddingBottom='20px'>
            If you are experiencing symptoms in an area that is not listed as an
            affected area, please call the EOC to ensure this is documented:
            448-3262, 449-1979, 448-2887, 448-2870, 448-2583. If you suspect you
            are affected
            <Link
              sx={{ paddingX: '5px' }}
              href='https://docs.google.com/forms/d/1I2GFxBWY91pfPfh7RY0ScDqp4t13jTVlieDXJuv-u3o/viewform?edit_requested=true'
            >
              submit the water survey
            </Link>
            and procure lodging (either self procured or government procured
            lodging).
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1a-content'
          id='panel1a-header'
        >
          <Typography>
            Where can I go for updates on the water situation?
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: '#f7f7f7' }}>
          <Typography paddingLeft='40px'>
            <Link href='https://go.usa.gov/xedX7'>
              https://go.usa.gov/xedX7
            </Link>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1a-content'
          id='panel1a-header'
        >
          <Typography>
            How many hotel rooms are my family authorized?
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: '#f7f7f7' }}>
          <Typography paddingLeft='40px' paddingBottom='20px'>
            0-3 dependents (not including sponsor) is authorized one room. 4 or
            more are authorized 2 rooms. Housing Assistance Center can only
            provide rooms based on the number of dependents in your household.
            Individuals that are not identified as dependents on your DEERS/Page
            2 will not be authorized to gain you additional rooms.
          </Typography>
        </AccordionDetails>
      </Accordion>
      {FAQ_ITEMS.map((faq_item, index) => (
        <Question
          key={index}
          question={faq_item.question}
          answer={faq_item.answer}
        />
      ))}
    </Box>
  );
};

export { Questions };
